import React, { useEffect } from 'react';
import Container from '@material-ui/core/Container';
import { useNavigate } from 'react-router-dom';
import { FormPage } from '../../../components/FormPage';
import { FormSection } from '../../../components/FormSection';
import Typography from '@material-ui/core/Typography/Typography';
import {
    SubmitPayoutRequestVariables,
    useInvoiceFileDelete,
    useS3PayoutUploadMutation,
    useSubmitPayoutRequest,
} from '../../../types/generated';
import { FullscreenLoader } from '../../../components/FullscreenLoader/FullscreenLoader';
import { Field } from 'react-final-form';
import { PdfUploadInput } from '../../../components/PdfUploadInput';
import * as Yup from 'yup';
import { UnclaimedPayoutMessage } from '../../../components/UnclaimedPayoutMessage';
import { useOrganizationStatus } from '../../../queries/useOrganizationStatus';
import { InvalidPayoutSettings } from './components/InvalidPayoutSettings';
import { InvalidTaxForm } from './components/InvalidTaxForm';
import { Pre } from './components/StyledPre';

const validationSchema = Yup.object().shape({
    invoice: Yup.object().required('You have to upload an invoice').nullable(),
});

export const PayoutRequestCreate = (): JSX.Element => {
    const {
        loading,
        hasUnclaimedPayout,
        periodFrom,
        periodTo,
        payoutSettingsPreview,
        displayAmount,
        hasValidTaxForm,
        hasValidPayoutSettings,
        refetch,
    } = useOrganizationStatus();
    const navigate = useNavigate();
    const [upload] = useS3PayoutUploadMutation();
    const [createPayoutRequest, { loading: loadingCreate }] = useSubmitPayoutRequest();
    const [deleteInvoice, { loading: deletingInvoice }] = useInvoiceFileDelete();

    useEffect(() => {
        refetch();
    }, [refetch]);

    const redirectBack = () => {
        navigate('/extranet/payouts');
    };

    const submit = (values): void => {
        const data: SubmitPayoutRequestVariables = {
            fileInput: {
                ...values.invoice,
                temp: false,
            },
        };

        createPayoutRequest({
            variables: data,
        }).then(() => {
            redirectBack();
        });
    };

    const getUploadParams = async ({ file, meta }) => {
        const { data: uploadData } = await upload();
        return {
            body: file,
            method: 'PUT',
            url: uploadData.s3UploadPayoutInvoice.node.uploadUrl,
            meta: { ...meta, ...uploadData.s3UploadPayoutInvoice.node.meta },
        };
    };

    if (loading) {
        return <FullscreenLoader />;
    }

    if (!hasValidTaxForm) {
        return <InvalidTaxForm />;
    }

    if (!hasValidPayoutSettings) {
        return <InvalidPayoutSettings />;
    }

    return (
        <Container maxWidth={'md'}>
            {hasUnclaimedPayout && periodFrom && periodTo && (
                <UnclaimedPayoutMessage
                    amount={displayAmount}
                    periodFrom={new Date(periodFrom)}
                    periodTo={new Date(periodTo)}
                />
            )}
            <FormPage
                title={'Create a payout request'}
                initialValues={{ invoice: null }}
                validationSchema={validationSchema}
                onSubmit={submit}
                loading={loading || loadingCreate}
                render={() => (
                    <React.Fragment>
                        <FormSection
                            title={'PDF Invoice'}
                            description={'Upload your PDF invoice for this payout request'}
                        >
                            <Field
                                name="invoice"
                                label="Upload invoice"
                                component={PdfUploadInput}
                                getUploadParams={getUploadParams}
                                multiple={false}
                                maxFiles={1}
                                onDelete={(s3Key, callback) => {
                                    deleteInvoice({
                                        variables: {
                                            fileKey: s3Key,
                                        },
                                    });
                                    callback();
                                }}
                                deleting={deletingInvoice}
                            />
                        </FormSection>
                        <FormSection
                            title={'Tax form and payout settings'}
                            description={
                                'A preview of your tax form and payout settings to be submitted with this request, make sure the information is correct and up to date.'
                            }
                        >
                            <Typography>
                                <strong>Preview:</strong>
                            </Typography>
                            <Pre
                                dangerouslySetInnerHTML={{
                                    __html: payoutSettingsPreview,
                                }}
                            />
                        </FormSection>
                    </React.Fragment>
                )}
            />
        </Container>
    );
};
